import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconButton } from '../components/IconButton';
import { IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface JournalEntryRow {
  id: number;
  journal_number: string;
  entry_date: string;
  description: string;
  entry_type: string;
  status: string;
  total_debit: string;
  lines_count: number;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  posted: 'Posted',
  exported: 'Exported',
  confirmed: 'Confirmed',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'badge-scheduled',
  posted: 'badge-neutral',
  exported: 'badge-neutral',
  confirmed: 'badge-active',
};

export function JournalEntries() {
  const { can } = useAuth();
  const { settings } = useConfig();
  const [status, setStatus] = useState('');

  const canCreate = can('journals.create');
  const filters = useMemo(() => ({ status: status || undefined }), [status]);
  const { data, loading, error, setPage } = usePaginatedList<JournalEntryRow>('/journal-entries', filters);

  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  return (
    <>
      <header className="page-header">
        <h1>Correcting Journal Entries</h1>
        {canCreate && (
          <div className="page-header-actions">
            <Link className="btn btn-ghost" to="/gl/journal-entries/new?contra=1">
              + New Contra Entry
            </Link>
            <Link className="btn btn-primary" to="/gl/journal-entries/new">
              + New Journal Entry
            </Link>
          </div>
        )}
      </header>
      <p className="muted">
        Manual corrections Finance keys directly against the GL — a separate, controlled process from the automated
        daily interface, requiring a different officer to finalize than the one who drafted it.
      </p>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Journal Number</th>
                <th>Date</th>
                <th>Description</th>
                <th>Type</th>
                <th>Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((entry) => (
                  <tr key={entry.id}>
                    <td className="muted">{entry.journal_number}</td>
                    <td className="muted">{entry.entry_date}</td>
                    <td>{entry.description}</td>
                    <td className="muted">{entry.entry_type === 'contra' ? 'Contra' : 'General'}</td>
                    <td>{fmtMoney(entry.total_debit)}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[entry.status] ?? 'badge-neutral'}`}>
                        {STATUS_LABELS[entry.status] ?? entry.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/gl/journal-entries/${entry.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No journal entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
